import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { OrderAPI } from '../services/api';
import { useLanguage } from '../components/Navbar';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Styled Components
const OrdersPageContainer = styled.div`
  min-height: 100vh;
  background: #0d0d0d;
  padding: 0;
`;

// Navigation Bar (Store style)
const NavigationBar = styled.nav`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid #f5f5f5;
  padding: 1rem 0;
  transition: all 0.3s ease;
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 300;
  letter-spacing: -0.02em;
  color: #1a1a1a;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: #d4af37;
    transform: scale(1.05);
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const OrdersButton = styled.button`
  background: transparent;
  border: none;
  outline: none;
  color: #000000;
  padding: 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: transform 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.75rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, #d4af37, #f4e4bc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CustomerIdForm = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid #333333;
`;

const FormTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1rem;
  text-align: center;
`;

const FormDescription = styled.p`
  color: #cfcfcf;
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Input = styled.input`
  flex: 1;
  padding: 1rem;
  border: 1px solid #333333;
  border-radius: 8px;
  background: #0d0d0d;
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
  
  &::placeholder {
    color: #666666;
  }
`;

const Button = styled.button`
  padding: 1rem 2rem;
  background: #d4af37;
  color: #1a1a1a;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #b8941f;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #666666;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const OrderCard = styled(motion.div)`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid #333333;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #d4af37;
    transform: translateY(-2px);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const OrderInfo = styled.div`
  flex: 1;
`;

const OrderId = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #d4af37;
  margin-bottom: 0.5rem;
`;

const OrderDate = styled.p`
  color: #cfcfcf;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`;

const OrderAmount = styled.p`
  color: #ffffff;
  font-size: 1.125rem;
  font-weight: 600;
`;

const StatusBadge = styled.div<{ status: string }>`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status) {
      case 'pending':
        return `
          background: #fff3cd;
          color: #856404;
        `;
      case 'confirmed':
        return `
          background: #d1ecf1;
          color: #0c5460;
        `;
      case 'shipped':
        return `
          background: #cce5ff;
          color: #004085;
        `;
      case 'delivered':
        return `
          background: #d4edda;
          color: #155724;
        `;
      case 'cancelled':
        return `
          background: #f8d7da;
          color: #721c24;
        `;
      default:
        return `
          background: #e2e3e5;
          color: #383d41;
        `;
    }
  }}
`;

const OrderDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  color: #666666;
  font-size: 0.875rem;
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: #ffffff;
  font-size: 0.875rem;
`;

const AdminNotes = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
`;

const AdminNotesTitle = styled.h4`
  color: #d4af37;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const AdminNotesText = styled.p`
  color: #cfcfcf;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666666;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  color: #ffffff;
  margin-bottom: 1rem;
`;

const EmptyDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #333333;
    border-top: 4px solid #d4af37;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const [customerId, setCustomerId] = useState('');
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

  const handleSearch = async () => {
    if (!customerId.trim()) {
      setError(language === 'tr' ? 'Lütfen müşteri kimlik numaranızı girin' : 'Please enter your customer ID');
      return;
    }

    setLoading(true);
    setError('');
    setSearched(true);

    try {
      // Önce admin kimlik numarası mı kontrol et
      const checkResponse = await fetch(`${API_URL}/check-customer-id`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customer_id: customerId.trim() }),
      });

      // Response durumunu kontrol et
      if (!checkResponse.ok) {
        throw new Error(`Sunucu hatası: ${checkResponse.status}. Lütfen backend'in çalıştığından emin olun.`);
      }

      const checkData = await checkResponse.json();

      if (checkData.success && checkData.is_admin) {
        // Admin kimlik numarası - admin paneline yönlendir
        localStorage.setItem('admin_customer_id', customerId.trim());
        setLoading(false);
        navigate('/admin');
        return;
      }

      // Normal müşteri - siparişleri getir
      const response = await OrderAPI.getOrderStatus(customerId.trim());
      setOrders(response.orders);
    } catch (error: any) {
      // Hata mesajını kontrol et - admin kimlik numarası mı?
      if (error.message && error.message.includes('Admin kimlik numarası')) {
        localStorage.setItem('admin_customer_id', customerId.trim());
        setLoading(false);
        navigate('/admin');
        return;
      }
      
      setError(error.message || (language === 'tr' ? 'Siparişler yüklenirken bir hata oluştu. Lütfen backend\'in çalıştığından emin olun.' : 'An error occurred while loading orders. Please make sure the backend is running.'));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status: string) => {
    if (language === 'tr') {
      const statusMap: { [key: string]: string } = {
        pending: 'Beklemede',
        confirmed: 'Onaylandı',
        shipped: 'Kargoya Verildi',
        delivered: 'Teslim Edildi',
        cancelled: 'İptal Edildi'
      };
      return statusMap[status] || status;
    } else {
      const statusMap: { [key: string]: string } = {
        pending: 'Pending',
        confirmed: 'Confirmed',
        shipped: 'Shipped',
        delivered: 'Delivered',
        cancelled: 'Cancelled'
      };
      return statusMap[status] || status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <OrdersPageContainer>
      <Helmet>
        <title>{language === 'tr' ? 'Siparişlerim - Barkın Sayın' : 'My Orders - Barkın Sayın'}</title>
        <meta name="description" content={language === 'tr' ? 'Siparişlerinizi takip edin' : 'Track your orders'} />
      </Helmet>

      {/* Navigation */}
      <NavigationBar>
        <NavContainer>
          <Logo onClick={() => navigate('/store')}>STORE</Logo>
          
          <NavRight>
            <OrdersButton onClick={() => navigate('/')}>
              {language === 'tr' ? 'Ana Sayfa' : 'Home'}
            </OrdersButton>
            <OrdersButton onClick={() => navigate('/orders')}>
              {language === 'tr' ? 'Siparişlerim' : 'My Orders'}
            </OrdersButton>
            <div style={{ position: 'relative' }}>
              <OrdersButton onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}>
                🌐 {language.toUpperCase()}
              </OrdersButton>
              {isLanguageDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  right: 0,
                  background: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  overflow: 'hidden',
                  minWidth: '140px',
                  zIndex: 1000
                }}>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setLanguage('tr'); setIsLanguageDropdownOpen(false); }} 
                    style={{
                      width: '100%',
                      padding: '12px 20px',
                      border: 'none',
                      background: language === 'tr' ? '#d4af37' : 'white',
                      color: language === 'tr' ? 'white' : '#1a1a1a',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: language === 'tr' ? '600' : '400',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => { if (language !== 'tr') e.currentTarget.style.background = '#f5f5f5'; }}
                    onMouseLeave={(e) => { if (language !== 'tr') e.currentTarget.style.background = 'white'; }}
                  >
                    🇹🇷 Türkçe
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setLanguage('en'); setIsLanguageDropdownOpen(false); }} 
                    style={{
                      width: '100%',
                      padding: '12px 20px',
                      border: 'none',
                      background: language === 'en' ? '#d4af37' : 'white',
                      color: language === 'en' ? 'white' : '#1a1a1a',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: language === 'en' ? '600' : '400',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => { if (language !== 'en') e.currentTarget.style.background = '#f5f5f5'; }}
                    onMouseLeave={(e) => { if (language !== 'en') e.currentTarget.style.background = 'white'; }}
                  >
                    🇬🇧 English
                  </button>
                </div>
              )}
            </div>
          </NavRight>
        </NavContainer>
      </NavigationBar>

      <Container>
        <Title>{language === 'tr' ? 'Siparişlerim' : 'My Orders'}</Title>

        <CustomerIdForm>
          <FormTitle>{language === 'tr' ? 'Müşteri Kimlik Numarası' : 'Customer ID'}</FormTitle>
          <FormDescription>
            {language === 'tr' 
              ? 'Siparişlerinizi görüntülemek için müşteri kimlik numaranızı girin. Bu numara sipariş tamamlandığında size verilmiştir.'
              : 'Enter your customer ID to view your orders. This number was given to you when your order was completed.'}
          </FormDescription>
          
          <InputGroup>
            <Input
              type="text"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="CUST-XXXXXX"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} disabled={loading}>
              {loading 
                ? (language === 'tr' ? 'Aranıyor...' : 'Searching...') 
                : (language === 'tr' ? 'Siparişleri Getir' : 'Get Orders')}
            </Button>
          </InputGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </CustomerIdForm>

        {loading && <LoadingSpinner />}

        {searched && !loading && orders.length === 0 && !error && (
          <EmptyState>
            <EmptyIcon>📦</EmptyIcon>
            <EmptyTitle>{language === 'tr' ? 'Sipariş Bulunamadı' : 'No Orders Found'}</EmptyTitle>
            <EmptyDescription>
              {language === 'tr' 
                ? 'Bu kimlik numarasına ait sipariş bulunamadı. Lütfen kimlik numaranızı kontrol edin.'
                : 'No orders found for this ID. Please check your customer ID.'}
            </EmptyDescription>
          </EmptyState>
        )}

        {orders.length > 0 && (
          <OrdersList>
            {orders.map((order, index) => (
              <OrderCard
                key={order.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <OrderHeader>
                  <OrderInfo>
                    <OrderId>{order.order_id}</OrderId>
                    <OrderDate>{formatDate(order.created_at)}</OrderDate>
                    <OrderAmount>₺{order.total_amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</OrderAmount>
                  </OrderInfo>
                  <StatusBadge status={order.status}>
                    {getStatusText(order.status)}
                  </StatusBadge>
                </OrderHeader>

                <OrderDetails>
                  <DetailItem>
                    <DetailLabel>Müşteri Kimlik No</DetailLabel>
                    <DetailValue>{order.customer_id}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Ad Soyad</DetailLabel>
                    <DetailValue>{order.first_name} {order.last_name}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Email</DetailLabel>
                    <DetailValue>{order.email}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Telefon</DetailLabel>
                    <DetailValue>{order.phone}</DetailValue>
                  </DetailItem>
                  {order.address && (
                    <DetailItem>
                      <DetailLabel>Adres</DetailLabel>
                      <DetailValue>{order.address}</DetailValue>
                    </DetailItem>
                  )}
                  {order.city && (
                    <DetailItem>
                      <DetailLabel>Şehir</DetailLabel>
                      <DetailValue>{order.city}</DetailValue>
                    </DetailItem>
                  )}
                  {order.receipt_filename && (
                    <DetailItem>
                      <DetailLabel>Dekont</DetailLabel>
                      <DetailValue>{order.receipt_filename}</DetailValue>
                    </DetailItem>
                  )}
                  {order.cargo_company && (
                    <DetailItem>
                      <DetailLabel>Kargo Şirketi</DetailLabel>
                      <DetailValue style={{ fontWeight: 600, color: '#d4af37' }}>
                        {order.cargo_company}
                      </DetailValue>
                    </DetailItem>
                  )}
                  {order.cargo_tracking_number && (
                    <DetailItem>
                      <DetailLabel>Kargo Takip No</DetailLabel>
                      <DetailValue style={{ fontWeight: 700, color: '#d4af37', fontSize: '1.1rem' }}>
                        {order.cargo_tracking_number}
                      </DetailValue>
                    </DetailItem>
                  )}
                </OrderDetails>

                {/* Sipariş Edilen Ürünler */}
                {order.cart_items && order.cart_items.length > 0 && (
                  <div style={{ 
                    marginTop: '1.5rem', 
                    padding: '1.5rem', 
                    background: 'rgba(212, 175, 55, 0.05)', 
                    borderRadius: '8px',
                    border: '1px solid rgba(212, 175, 55, 0.2)'
                  }}>
                    <h4 style={{ color: '#d4af37', marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>
                      📦 Sipariş Edilen Ürünler
                    </h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'rgba(212, 175, 55, 0.1)', borderBottom: '2px solid rgba(212, 175, 55, 0.3)' }}>
                          <th style={{ padding: '10px', textAlign: 'left', color: '#d4af37', fontSize: '0.875rem' }}>Ürün</th>
                          <th style={{ padding: '10px', textAlign: 'center', color: '#d4af37', fontSize: '0.875rem' }}>Adet</th>
                          <th style={{ padding: '10px', textAlign: 'right', color: '#d4af37', fontSize: '0.875rem' }}>Fiyat</th>
                          <th style={{ padding: '10px', textAlign: 'right', color: '#d4af37', fontSize: '0.875rem' }}>Toplam</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.cart_items.map((item: any, index: number) => (
                          <tr key={index} style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                            <td style={{ padding: '10px', color: '#cfcfcf', fontSize: '0.875rem' }}>{item.product_name}</td>
                            <td style={{ padding: '10px', textAlign: 'center', color: '#cfcfcf', fontSize: '0.875rem' }}>{item.quantity}</td>
                            <td style={{ padding: '10px', textAlign: 'right', color: '#cfcfcf', fontSize: '0.875rem' }}>₺{item.price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '10px', textAlign: 'right', color: '#cfcfcf', fontSize: '0.875rem' }}>₺{(item.price * item.quantity).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {order.admin_notes && (
                  <AdminNotes>
                    <AdminNotesTitle>Admin Notları</AdminNotesTitle>
                    <AdminNotesText>{order.admin_notes}</AdminNotesText>
                  </AdminNotes>
                )}
              </OrderCard>
            ))}
          </OrdersList>
        )}
      </Container>
    </OrdersPageContainer>
  );
};

export default Orders;
